import { useReducer, useEffect } from 'react'
import moment from 'moment'
import api, { Order } from '@api'
import { ILease, LeaseDateFilterKey, ILeaseKeyValueFilter, ILeaseListVariables } from '@api'
import { ISort, IPage } from '@ui/Table'
import { IDateRangePreset } from '../DateFilter'
import { notEmpty } from '@utils'
import { Toaster } from '@ui'
import { ApolloError } from 'apollo-boost'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

enum Actions {
  CLEAR_FILTERS,
  FILTER,
  LOAD,
  LOAD_CSV_LINK,
  NEXT,
  QUERY,
  SORT,
}

interface IState {
  after: string
  first: number
  filters: {
    query: string
    city: string | null
    date: {
      endDate: IDateFilter | null
      noticeDate: IDateFilter | null
      startDate: IDateFilter | null
    }
  }
  exportList: string
  counters: ICounters
  items: ILease[]
  page: IPage
  isLoading: boolean
  sort: ISort
}
interface ICounters {
  leases: number
  totalAnnualCost: number
}
export interface ILeases extends IState {
  queryTable: (query: string) => void
  reload: () => void
  nextPage: () => void
  clearFilters: () => void
  fetchExportLink: () => void
  filter: (filter: IFilter) => void
}
const persistedState: IState = {
  items: [],
  after: '',
  first: PAGE_SIZE,
  counters: {
    leases: 0,
    totalAnnualCost: 0,
  },
  exportList: '',
  filters: {
    query: '',
    city: null,
    date: {
      endDate: {
        key: 'endDate',
        kind: 'date',
        value: {
          fromDate: moment().toDate(),
          toDate: undefined,
          label: 'Select',
        },
      },
      noticeDate: null,
      startDate: null,
    },
  },
  sort: {
    column: '',
    order: Order.ASC,
    isSorting: false,
    onChange: () => undefined,
  },
  page: {
    hasNextPage: false,
    next: () => undefined,
    total: 0,
    endCursor: '',
    isPaging: false,
  },
  isLoading: true,
}

export interface ICityFilter {
  kind: 'city'
  city: string | null
}
export interface IDateFilter {
  kind: 'date'
  key: LeaseDateFilterKey
  value: IDateRangePreset | null
}

type IFilter = ILeaseKeyValueFilter | ICityFilter | IDateFilter

function reducer(state: IState, action: any) {
  switch (action.type) {
    case Actions.LOAD:
      const leases = action.leases.map((e: any) => e.node)

      return {
        ...state,
        items: state.page.isPaging ? [...state.items, ...leases] : leases,
        exportList: action.exportList,
        counters: action.counters,
        page: {
          ...state.page,
          endCursor: action.pageInfo.endCursor,
          hasNextPage: action.pageInfo.hasNextPage,
          isPaging: false,
        },
        sort: {
          ...state.sort,
          isSorting: false,
        },
        isLoading: false,
      }
    case Actions.QUERY:
      return {
        ...state,
        filters: {
          ...state.filters,
          query: action.query,
        },
      }
    case Actions.CLEAR_FILTERS:
      return {
        ...state,
        filters: {
          query: '',
          city: null,
          date: {
            startDate: null,
            endDate: null,
            noticeDate: null,
          },
        },
      }
    case Actions.LOAD_CSV_LINK:
      return {
        ...state,
        exportList: action.exportList,
      }
    case Actions.FILTER:
      switch (action.filter.kind) {
        case 'city':
          return {
            ...state,
            filters: {
              ...state.filters,
              city: action.filter.city,
            },
          }
        case 'date':
          return {
            ...state,
            filters: {
              ...state.filters,
              date: {
                ...state.filters.date,
                [action.filter.key]: action.filter,
              },
            },
          }
        default:
          return state
      }
    case Actions.NEXT:
      return {
        ...state,
        after: state.page.endCursor,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
        page: {
          ...state.page,
          isPaging: true,
        },
      }
    case Actions.SORT:
      return {
        ...state,
        sort: {
          ...state.sort,
          order:
            action.column === state.sort.column
              ? state.sort.order === Order.DESC
                ? Order.ASC
                : Order.DESC
              : Order.DESC,
          column: action.column,
          isSorting: true,
        },
      }
    default:
      return state
  }
}

export default function useLeases(): ILeases {
  const [state, dispatch] = useReducer(reducer, persistedState)

  const prepVariables = (): ILeaseListVariables => {
    const variables: ILeaseListVariables = {
      query: state.filters.query,
      first: state.first,
      after: state.after,
      dateFilters: [],
      cities: [state.filters.city].filter(notEmpty),
    }
    if (state.sort.column) {
      variables.sortBy = state.sort.column.replace(/~/g, '')
      variables.order = state.sort.order
    }

    const dateFilters: IDateFilter[] = Object.values(state.filters.date).filter(notEmpty)
    for (const { key, value } of dateFilters) {
      if (!value || (!value.fromDate && !value.toDate)) continue
      variables.dateFilters.push({
        key,
        value: {
          fromDate: value.fromDate ? moment(value.fromDate).format('YYYY-MM-DD') : undefined,
          toDate: value.toDate ? moment(value.toDate).format('YYYY-MM-DD') : undefined,
        },
      })
    }
    return variables
  }

  const makeLoadMethod =
    ({ loadAll }: { loadAll: boolean }) =>
    () => {
      const variables = prepVariables()

      api.lease
        .list({
          ...variables,
          first: loadAll ? state.first : PAGE_SIZE,
          after: loadAll ? null : state.after,
        })
        .then((leasesConnection) => {
          dispatch({
            type: Actions.LOAD,
            leases: leasesConnection.edges,
            pageInfo: leasesConnection.pageInfo,
            counters: leasesConnection.counters,
            exportList: leasesConnection.exportList,
          })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    }

  const fetchExportLink = () => {
    const variables = prepVariables()
    api.lease.csv(variables).then(({ exportList }) => {
      dispatch({ type: Actions.LOAD_CSV_LINK, exportList })
    })
  }

  //load all items according state.first prop
  useEffect(makeLoadMethod({ loadAll: true }), [state.sort.order, state.sort.column, state.filters])

  //Load +25 items
  useEffect(makeLoadMethod({ loadAll: false }), [state.after])

  state.sort.onChange = (column) => dispatch({ type: Actions.SORT, column })
  state.page.next = () => dispatch({ type: Actions.NEXT })

  return {
    ...state,
    filter: (filter: IFilter) => dispatch({ type: Actions.FILTER, filter }),
    queryTable: (query: string) => {
      dispatch({ type: Actions.QUERY, query })
    },
    reload: makeLoadMethod({ loadAll: true }),
    clearFilters: () => dispatch({ type: Actions.CLEAR_FILTERS }),
    nextPage: () => dispatch({ type: Actions.NEXT }),
    fetchExportLink,
  }
}
